import {
  AppstoreOutlined,
  BarsOutlined,
  FilterOutlined,
  TableOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Button, Divider, Input, Segmented, Select, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetMediaTags } from '../../../../../utils/api/medias/useGetMediaTags';
import {
  MAX_SELECTED_MEDIAS,
  MEDIA_QUALIFICATION_SORT_OPTIONS,
  MEDIAS_TYPES_OPTIONS,
} from '../../../../../utils/const/medias';
import { useMediaToQualifyFilters } from '../../../../../utils/zustand/hooks/useMediaToQualifyFilters';
import { useMediaToQualifyFiltersStore } from '../../../../../utils/zustand/stores/mediaToQualifyFiltersStore';
import TagDrawer from '../TagDrawer';
import FilterDrawer from './FilterDrawer';

const { Search } = Input;

const MediaFilters = ({
  searchMedia,
  onSearchMediaChange,
  viewMode = 'large',
  onViewModeChange,
  onSelectAllMedias,
}) => {
  const { filters, setFilter } = useMediaToQualifyFiltersStore();
  const { selectedType, selectedTags, sortBy } = filters;

  const { setSelectedTagsFilter, setSelectedTypeFilter } =
    useMediaToQualifyFilters();

  const setSortBy = (value) => setFilter('sortBy', value);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tagDrawerOpen, setTagDrawerOpen] = useState(false);

  const [searchText, setSearchText] = useState(searchMedia);

  useEffect(() => {
    setSearchText(searchMedia);
  }, [searchMedia]);

  const [searchedTag, setSearchedTag] = useState('');
  const [debouncedSearchTag, setDebouncedSearchTag] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTag(searchedTag.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchedTag]);

  const { tags, isLoading: isTagLoading } = useGetMediaTags({
    search: debouncedSearchTag,
  });

  const tagOptions = useMemo(() => {
    const default_option = [{ label: 'Tous les tags', value: 'all' }];
    if (tags) {
      const options_tags = tags.map((tag) => ({
        label: tag.name,
        value: tag.name,
      }));
      const options = default_option.concat(options_tags);

      return options;
    }
    return default_option;
  }, [tags]);

  return (
    <div style={{ padding: '8px 16px 8px 16px', backgroundColor: '#fff' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
          <Search
            size="small"
            placeholder="Rechercher des médias..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={onSearchMediaChange}
            allowClear
            style={{ flex: 1 }}
          />
          <Divider
            type="vertical"
            style={{ height: '32px', margin: '0 8px' }}
          />

          <Select
            size="small"
            placeholder="Trier par"
            value={sortBy}
            onChange={setSortBy}
            options={MEDIA_QUALIFICATION_SORT_OPTIONS}
            style={{ width: 160, flexShrink: 0 }}
          />
        </div>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size="middle">
            <Select
              size="small"
              placeholder="Type"
              value={selectedType}
              onChange={setSelectedTypeFilter}
              options={MEDIAS_TYPES_OPTIONS}
              style={{ minWidth: 140 }}
              allowClear
            />

            <Select
              size="small"
              placeholder="Tag"
              value={selectedTags}
              onChange={setSelectedTagsFilter}
              options={tagOptions}
              style={{ minWidth: 140 }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              searchValue={searchedTag}
              onSearch={setSearchedTag}
              loading={isTagLoading}
              allowClear
            />

            <Button
              size="small"
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Voir plus
            </Button>

            <Divider
              type="vertical"
              style={{ height: '32px', margin: '0 8px' }}
            />

            <Segmented
              size="small"
              value={viewMode}
              onChange={onViewModeChange}
              options={[
                {
                  value: 'large',
                  icon: <AppstoreOutlined style={{ fontSize: '18px' }} />,
                },
                {
                  value: 'small',
                  icon: <TableOutlined style={{ fontSize: '18px' }} />,
                },
                {
                  value: 'list',
                  icon: <BarsOutlined style={{ fontSize: '18px' }} />,
                },
              ]}
            />
            <Button size="small" onClick={onSelectAllMedias}>
              Sélectionner les {MAX_SELECTED_MEDIAS} premiers
            </Button>
          </Space>

          <Button
            size="small"
            type="primary"
            icon={<TagsOutlined />}
            onClick={() => setTagDrawerOpen(true)}
          >
            Gérer les tags
          </Button>
        </Space>
      </Space>

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      />

      <TagDrawer open={tagDrawerOpen} onClose={() => setTagDrawerOpen(false)} />
    </div>
  );
};

export default MediaFilters;
